import { HttpClient, HttpHeaders, HttpParams, HttpUrlEncodingCodec } from '@angular/common/http';
import { inject, Injectable, NgZone } from '@angular/core';
import { AuthResult, ProviderOptions } from '@ionic-enterprise/auth';
import { jwtDecode } from 'jwt-decode';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';

import { fireAndForget, wait } from '../../../helpers';
import { OidcConfigDocument, TokenResponse } from '../../../models';
import { ApiService, AuthConfigReaderService, ConfigSettingsService, LogService } from '../../../providers';
import { AuthSessionService, DecodedOAuthAccessToken } from '../../auth';
import { Authenticator } from '../interface';

@Injectable({
  providedIn: 'root',
})
export class PasswordGrantAuthService implements Authenticator {
  private readonly authConfigService = inject(AuthConfigReaderService);
  private readonly authSession = inject(AuthSessionService);
  private readonly configService = inject(ConfigSettingsService);
  private readonly http = inject(HttpClient);
  private readonly ngZone = inject(NgZone);

  private readonly authenticationChange: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private readonly oauthScopes = 'res.api offline_access';

  public readonly api = inject(ApiService);
  public authenticationChange$: Observable<boolean>;

  private _oidcConfigDocument!: OidcConfigDocument;
  private _providerOptions!: ProviderOptions;

  constructor() {
    this.authenticationChange$ = this.authenticationChange.asObservable();
  }

  private get authBaseAddress(): string | null {
    return this.configService.appSettings.connection?.auth;
  }

  public async authenticate(username: string, password: string, releaseCode?: string): Promise<void> {
    const token = await this.getToken(username, password, releaseCode);
    if ('error' in token) {
      throw token.error;
    }

    token.sec ??= password;
    const authResult = this.createAuthResultFromTokenResponse(token) as AuthResult;
    await this.saveAuthResult(authResult);
  }

  public async getAccessToken(): Promise<string | undefined> {
    const res = await this.getAuthResult();
    return res?.accessToken;
  }

  public async initialize(): Promise<void> {
    await this.setupProviderAndOptions();
  }

  public async isAuthenticated(): Promise<boolean> {
    const authResult = await this.getAuthResult();
    return !!authResult && this.isAccessTokenAvailable(authResult);
  }

  public async logout(): Promise<void> {
    await this.revokeRefreshToken();
    await this.authSession.clear();
    await this.onAuthChange(false);
  }

  private createAuthResultFromTokenResponse(
    token: TokenResponse,
  ): Omit<AuthResult, 'provider' | 'config' | 'idToken' | 'state'> {
    return {
      receivedAt: Date.now(),
      accessToken: token.access_token,
      expiresIn: token.expires_in,
      tokenType: token.token_type,
      refreshToken: token.refresh_token,
      scope: this.oauthScopes,
      rawResult: JSON.stringify(token),
    };
  }

  private async getAuthResult(): Promise<AuthResult | null> {
    try {
      const authResult = await this.authSession.getAuthResult();

      if (!authResult) {
        await this.onAuthChange(false);
        return null;
      }

      const isAccessTokenAvailable = this.isAccessTokenAvailable(authResult);
      const isAccessTokenExpired = this.isAccessTokenExpired(authResult);

      if (isAccessTokenAvailable && isAccessTokenExpired) {
        return await this.refreshAuthResult(authResult);
      }

      await this.onAuthChange(true);

      return authResult;
    } catch (error) {
      LogService.error(`${error}`);
      return null;
    }
  }

  private getExpirationTime(token: string): number | null {
    try {
      const decoded: DecodedOAuthAccessToken = jwtDecode(token);
      if (!decoded.exp) {
        return null;
      }

      return decoded.exp;
    } catch (error) {
      console.error('Token Decoding Error:', error);
      return null;
    }
  }

  private getHttpParamsInstance(): HttpParams {
    return new HttpParams({
      encoder: new (class extends HttpUrlEncodingCodec {
        public override encodeValue(value: string): string {
          return encodeURIComponent(value);
        }
      })(),
    });
  }

  private async getOidcConfigurationDocument(): Promise<OidcConfigDocument> {
    if (!this._oidcConfigDocument) {
      const url = `${this.authBaseAddress}/.well-known/openid-configuration`;
      this._oidcConfigDocument = await firstValueFrom(this.http.get<OidcConfigDocument>(url));
    }

    return this._oidcConfigDocument;
  }

  private async getToken(
    username: string,
    password: string,
    releaseCode?: string,
  ): Promise<TokenResponse | { error: string }> {
    const params = new URLSearchParams();
    params.append('username', username);
    params.append('password', password);
    params.append('grant_type', 'password');
    params.append('client_id', this._providerOptions.clientId);
    params.append('client_secret', this.configService.appSettings.connection.clientSecret || '');

    const headers = new HttpHeaders({ 'Content-type': 'application/x-www-form-urlencoded; charset=utf-8' });

    if (releaseCode) {
      params.set('release_code', releaseCode);
    }

    return firstValueFrom(this.api.doPost(await this.getTokenApiUrl(), params.toString(), { headers }));
  }

  private async getTokenApiUrl(): Promise<string> {
    const oidDoc = await this.getOidcConfigurationDocument();
    return oidDoc.token_endpoint;
  }

  private isAccessTokenAvailable(authResult: AuthResult): boolean {
    return authResult.accessToken != null;
  }

  private isAccessTokenExpired(authResult: AuthResult): boolean {
    const expirationTime = authResult.accessToken ? this.getExpirationTime(authResult.accessToken) : null;

    if (!expirationTime) {
      console.error('no expiration date found in auth token');
      return true;
    }

    const currentTime = Math.floor(Date.now() / 1000);
    return currentTime >= expirationTime;
  }

  private isRefreshTokenAvailable(authResult: AuthResult): boolean {
    return authResult.refreshToken != null;
  }

  private async onAuthChange(isAuthenticated: boolean): Promise<void> {
    this.ngZone.run(() => this.authenticationChange.next(isAuthenticated));
  }

  private async refreshAuthResult(authResult: AuthResult): Promise<AuthResult | null> {
    let newAuthResult: AuthResult | null = null;
    if (this.isRefreshTokenAvailable(authResult)) {
      try {
        const tokenResponse = await this.refreshSession(authResult);
        newAuthResult = this.createAuthResultFromTokenResponse(tokenResponse) as AuthResult;
      } catch (err) {
        console.error(err);
      }
    }
    await this.saveAuthResult(newAuthResult);
    return newAuthResult;
  }

  private async refreshSession(authResult: AuthResult): Promise<TokenResponse> {
    const tokenEndpoint = (await this.authConfigService.getTokenEndpoint()) || '';
    const token = authResult.refreshToken || '';
    const { clientId, clientSecret } = this.configService.appSettings.connection;
    const headers = new HttpHeaders({ 'Content-type': 'application/x-www-form-urlencoded; charset=utf-8' });
    const body = new HttpParams()
      .set('client_id', clientId)
      .set('client_secret', clientSecret || '')
      .set('grant_type', 'refresh_token')
      .set('refresh_token', token);

    return firstValueFrom(this.api.doPost<TokenResponse>(tokenEndpoint, body, { headers }));
  }

  private async revokeRefreshToken(): Promise<void> {
    const revocationUrl = await this.authConfigService.createRevocationUrl();
    if (!revocationUrl) {
      return;
    }

    const token = (await this.getAuthResult())?.refreshToken;
    if (!token) {
      return;
    }

    const { clientId, clientSecret } = this.configService.appSettings.connection;
    const headers = { 'Content-type': 'application/x-www-form-urlencoded; charset=utf-8' };
    const body = this.getHttpParamsInstance()
      .set('client_id', clientId)
      .set('client_secret', clientSecret || '')
      .set('token', token);

    return firstValueFrom(this.http.post(revocationUrl, body, { headers }).pipe(fireAndForget()), {
      defaultValue: undefined,
    });
  }

  private async saveAuthResult(authResult: AuthResult | null): Promise<void> {
    if (authResult) {
      await this.authSession.saveUserDataFromAuthResult(authResult);
      await this.authSession.setAuthResult(authResult);
    } else {
      await this.authSession.clear();
    }

    await this.onAuthChange(authResult !== null);
  }

  private async setupProviderAndOptions(): Promise<void> {
    if (!this.authBaseAddress) {
      LogService.info('no auth config base address');
      await wait(1);
      return this.setupProviderAndOptions();
    }

    this._providerOptions = {
      audience: '',
      clientId: this.configService.appSettings.connection.clientId,
      discoveryUrl: `${this.authBaseAddress}/.well-known/openid-configuration`,
      redirectUri: this.configService.appSettings.connection.redirectUri || '',
      logoutUrl: this.configService.appSettings.connection.postLogoutRedirectUri || '',
      scope: this.oauthScopes,
    };
  }
}
